import { useState, useEffect } from 'react';
import { useSyncDetails } from '@/hooks/useSyncHistory';
import type { SyncSuccessDetail, SyncErrorDetail } from '@/types';
import { cn } from '@/utils/cn';
import {
  X,
  ExternalLink,
  RotateCcw,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface SyncDetailModalProps {
  syncHistoryId: string;
  onClose: () => void;
  onRetry?: (syncHistoryId: string) => void;
}

/**
 * Modal for displaying detailed sync results (both successes and errors) with deep links
 */
export function SyncDetailModal({
  syncHistoryId,
  onClose,
  onRetry,
}: SyncDetailModalProps) {
  const { details, isLoading, error: fetchError, refetch } = useSyncDetails(syncHistoryId);
  const [retrying, setRetrying] = useState<string[]>([]);
  const [showSuccesses, setShowSuccesses] = useState(true);
  const [showErrors, setShowErrors] = useState(true);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleRetryAll = async () => {
    if (!onRetry || !details) return;

    setRetrying(details.errors.map(e => e.entityId));

    try {
      await onRetry(syncHistoryId);
    } finally {
      setRetrying([]);
    }
  };

  const getEntityTypeLabel = (entityType: string) => {
    const labels: Record<string, string> = {
      contact: 'Contact',
      product: 'Product',
      invoice: 'Invoice',
      contacts: 'Contact',
      products: 'Product',
      invoices: 'Invoice',
    };
    return labels[entityType] || entityType;
  };

  const getSystemLabel = (system: string) => {
    return system === 'highlevel' ? 'HighLevel' : 'Xero';
  };

  const getOperationLabel = (operation: string) => {
    const labels: Record<string, string> = {
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
      synced: 'Synced',
    };
    return labels[operation] || operation;
  };

  const getOperationColor = (operation: string) => {
    switch (operation) {
      case 'create':
        return 'bg-green-100 text-green-700';
      case 'update':
        return 'bg-blue-100 text-blue-700';
      case 'delete':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-start justify-between">
              <div>
                <h3
                  className="text-lg font-semibold leading-6 text-gray-900"
                  id="modal-title"
                >
                  Sync Details
                </h3>
                {details && (
                  <p className="mt-1 text-sm text-gray-500">
                    {details.summary.successfulRecords} synced, {details.summary.failedRecords} failed of {details.summary.totalRecords} total
                  </p>
                )}
              </div>
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading details...</span>
                </div>
              ) : fetchError ? (
                <div className="text-center py-8">
                  <p className="text-red-500">Failed to load sync details</p>
                  <button
                    onClick={() => refetch()}
                    className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Retry
                  </button>
                </div>
              ) : !details || (details.successes.length === 0 && details.errors.length === 0) ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No sync details available yet</p>
                  <p className="text-sm text-gray-400 mt-1">Details will appear as items are processed</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Successes Section */}
                  {details.successes.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowSuccesses(!showSuccesses)}
                        className="flex items-center justify-between w-full py-2 text-left"
                      >
                        <div className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                          <span className="font-medium text-gray-900">
                            Successful ({details.successes.length})
                          </span>
                        </div>
                        {showSuccesses ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </button>

                      {showSuccesses && (
                        <div className="max-h-60 overflow-y-auto space-y-2 mt-2">
                          {details.successes.map((success, index) => (
                            <SuccessItem
                              key={`${success.entityId}-${index}`}
                              success={success}
                              getEntityTypeLabel={getEntityTypeLabel}
                              getSystemLabel={getSystemLabel}
                              getOperationLabel={getOperationLabel}
                              getOperationColor={getOperationColor}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Errors Section */}
                  {details.errors.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowErrors(!showErrors)}
                        className="flex items-center justify-between w-full py-2 text-left"
                      >
                        <div className="flex items-center">
                          <XCircle className="h-5 w-5 text-red-500 mr-2" />
                          <span className="font-medium text-gray-900">
                            Failed ({details.errors.length})
                          </span>
                        </div>
                        {showErrors ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </button>

                      {showErrors && (
                        <div className="max-h-60 overflow-y-auto space-y-2 mt-2">
                          {details.errors.map((error, index) => (
                            <ErrorItem
                              key={`${error.entityId}-${index}`}
                              error={error}
                              isRetrying={retrying.includes(error.entityId)}
                              getEntityTypeLabel={getEntityTypeLabel}
                              getSystemLabel={getSystemLabel}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
            {onRetry && details && details.errors.length > 0 && (
              <button
                type="button"
                onClick={handleRetryAll}
                disabled={retrying.length > 0}
                className={cn(
                  'inline-flex w-full justify-center items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto',
                  'bg-blue-600 text-white hover:bg-blue-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {retrying.length > 0 ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retry Failed ({details.errors.length})
                  </>
                )}
              </button>
            )}
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Individual success item in the list
 */
function SuccessItem({
  success,
  getEntityTypeLabel,
  getSystemLabel,
  getOperationLabel,
  getOperationColor,
}: {
  success: SyncSuccessDetail;
  getEntityTypeLabel: (type: string) => string;
  getSystemLabel: (system: string) => string;
  getOperationLabel: (op: string) => string;
  getOperationColor: (op: string) => string;
}) {
  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-3">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-900">
              {success.entityName || success.entityId}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {getEntityTypeLabel(success.entityType)}
            </span>
            <span className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              getOperationColor(success.operation)
            )}>
              {getOperationLabel(success.operation)}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {getSystemLabel(success.sourceSystem)} &rarr; {getSystemLabel(success.targetSystem)}
          </p>
          {success.timestamp && (
            <p className="mt-1 text-xs text-gray-400">
              {new Date(success.timestamp).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-2 ml-2">
          {success.sourceDeepLink && (
            <a
              href={success.sourceDeepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
              title={`View in ${getSystemLabel(success.sourceSystem)}`}
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {success.targetDeepLink && (
            <a
              href={success.targetDeepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
              title={`View in ${getSystemLabel(success.targetSystem)}`}
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Individual error item in the list
 */
function ErrorItem({
  error,
  isRetrying,
  getEntityTypeLabel,
  getSystemLabel,
}: {
  error: SyncErrorDetail;
  isRetrying: boolean;
  getEntityTypeLabel: (type: string) => string;
  getSystemLabel: (system: string) => string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-red-200 bg-red-50 p-3',
        isRetrying && 'opacity-50'
      )}
    >
      <div className="flex items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-900">
              {getEntityTypeLabel(error.entityType)}
            </span>
            <span className="text-xs text-gray-500 font-mono">
              {error.entityId}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {getSystemLabel(error.sourceSystem)}
            </span>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {error.errorMessage}
          </p>
          {error.timestamp && (
            <p className="mt-1 text-xs text-gray-400">
              {new Date(error.timestamp).toLocaleString()}
            </p>
          )}
          {error.deepLink && (
            <a
              href={error.deepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              View in {getSystemLabel(error.sourceSystem)}
            </a>
          )}
        </div>
        {isRetrying && (
          <Loader2 className="h-4 w-4 animate-spin text-blue-500 ml-2" />
        )}
      </div>
    </div>
  );
}
